/**
 * Child Service Report Summary Tab
 * Shows which Full Time children have/haven't completed their annual service report
 * Embedded inside the Compliance Dashboard "Child Service Reports" item
 */
import React, { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import axios from "axios";
import { Printer, CheckCircle2, XCircle, AlertTriangle, Loader2, Users, RefreshCw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ChildServiceReportSummaryTab = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const printRef = useRef(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/compliance/child-service-report-status`);
      setData(res.data);
    } catch (err) {
      toast.error("Failed to load service report data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handlePrint = () => {
    if (!printRef.current) return;
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Child Service Report Summary</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 0.5in; color: #333; }
            .logo-header { margin-bottom: 12px; }
            .logo-header img { height: 40px; }
            h1 { font-size: 16pt; color: #1a365d; margin: 0 0 4px 0; }
            h2 { font-size: 13pt; color: #1a365d; margin: 8px 0; }
            .subtitle { font-size: 9pt; color: #666; margin-bottom: 12px; }
            .summary-bar { display: flex; gap: 16px; margin: 12px 0; padding: 10px; background: #f0fdf4; border: 1px solid #bbf7d0; border-radius: 6px; }
            .summary-bar.non-compliant { background: #fef2f2; border-color: #fecaca; }
            .stat { text-align: center; flex: 1; }
            .stat-num { font-size: 20pt; font-weight: bold; }
            .stat-label { font-size: 8pt; color: #666; }
            .compliant { color: #16a34a; }
            .missing { color: #dc2626; }
            .expiring { color: #d97706; }
            table { width: 100%; border-collapse: collapse; margin-top: 10px; font-size: 9pt; }
            th { background: #e0e7ff; color: #1e40af; padding: 6px 8px; text-align: left; border: 1px solid #c7d2fe; }
            td { padding: 5px 8px; border: 1px solid #e5e7eb; }
            tr:nth-child(even) { background: #f9fafb; }
            .status-compliant { color: #16a34a; font-weight: bold; }
            .status-missing { color: #dc2626; font-weight: bold; }
            .status-expired { color: #dc2626; font-weight: bold; }
            .status-expiring { color: #d97706; font-weight: bold; }
          </style>
        </head>
        <body>
          <div class="logo-header"><img src="/simple-sign-in-logo.png" onerror="this.style.display='none'" /></div>
          ${printRef.current.innerHTML}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
        <span className="ml-2 text-sm text-gray-500">Loading service report data...</span>
      </div>
    );
  }

  if (!data) return null;

  // Filter to Full Time only, excluding archived children
  const fullTimeChildren = (data.children || []).filter(c => 
    (c.category || '').toLowerCase() === 'full_time' && c.active !== false
  );

  const compliant = fullTimeChildren.filter(c => c.status === 'compliant');
  const expired = fullTimeChildren.filter(c => c.status === 'expired');
  const expiring = fullTimeChildren.filter(c => c.status === 'expiring');
  const missing = fullTimeChildren.filter(c => c.status === 'missing');
  const total = fullTimeChildren.length;
  const compliantCount = compliant.length;
  const isFullyCompliant = total > 0 && compliantCount === total && expired.length === 0;
  const pct = total > 0 ? Math.round((compliantCount / total) * 100) : 0;

  return (
    <div className="space-y-4" data-testid="child-service-report-summary">
      {/* Action bar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Users className="w-5 h-5 text-blue-600" />
          <span className="text-sm font-semibold text-blue-800">Full Time Children: {total}</span>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={fetchData} className="text-xs">
            <RefreshCw className="w-3 h-3 mr-1" /> Refresh
          </Button>
          <Button size="sm" variant="outline" onClick={handlePrint} className="text-xs">
            <Printer className="w-3 h-3 mr-1" /> Print Report
          </Button>
        </div>
      </div>

      {/* Compliance status banner */}
      {isFullyCompliant ? (
        <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
          <CheckCircle2 className="w-5 h-5 text-green-600" />
          <span className="text-sm font-semibold text-green-700">Compliant — All {total} full-time children have current service reports</span>
        </div>
      ) : (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
          <XCircle className="w-5 h-5 text-red-600" />
          <span className="text-sm font-semibold text-red-700">Non-Compliant — {compliantCount}/{total} full-time children have current reports ({pct}%)</span>
        </div>
      )}

      {/* Stats row */}
      <div className="grid grid-cols-4 gap-2 text-center">
        <div className="p-2 bg-green-50 rounded-lg border border-green-200">
          <p className="text-lg font-bold text-green-700">{compliantCount}</p>
          <p className="text-[10px] text-green-600">Current</p>
        </div>
        <div className="p-2 bg-amber-50 rounded-lg border border-amber-200">
          <p className="text-lg font-bold text-amber-700">{expiring.length}</p>
          <p className="text-[10px] text-amber-600">Expiring Soon</p>
        </div>
        <div className="p-2 bg-red-50 rounded-lg border border-red-200">
          <p className="text-lg font-bold text-red-700">{expired.length}</p>
          <p className="text-[10px] text-red-600">Expired</p>
        </div>
        <div className="p-2 bg-gray-50 rounded-lg border border-gray-200">
          <p className="text-lg font-bold text-gray-700">{missing.length}</p>
          <p className="text-[10px] text-gray-600">Missing</p>
        </div>
      </div>

      {/* Printable content */}
      <div ref={printRef}>
        <h1 style={{ fontSize: '14pt', color: '#1a365d', margin: '0 0 4px 0' }}>Child Service Report Summary</h1>
        <p className="subtitle" style={{ fontSize: '9pt', color: '#666', marginBottom: '8px' }}>
          Full Time Children Only | Generated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
        </p>

        <div style={{ display: 'flex', gap: '12px', margin: '10px 0', padding: '8px', background: isFullyCompliant ? '#f0fdf4' : '#fef2f2', border: `1px solid ${isFullyCompliant ? '#bbf7d0' : '#fecaca'}`, borderRadius: '6px' }}>
          <div style={{ flex: 1, textAlign: 'center' }}>
            <div style={{ fontSize: '18pt', fontWeight: 'bold', color: '#16a34a' }}>{compliantCount}</div>
            <div style={{ fontSize: '8pt', color: '#666' }}>Current</div>
          </div>
          <div style={{ flex: 1, textAlign: 'center' }}>
            <div style={{ fontSize: '18pt', fontWeight: 'bold', color: '#d97706' }}>{expiring.length}</div>
            <div style={{ fontSize: '8pt', color: '#666' }}>Expiring</div>
          </div>
          <div style={{ flex: 1, textAlign: 'center' }}>
            <div style={{ fontSize: '18pt', fontWeight: 'bold', color: '#dc2626' }}>{expired.length}</div>
            <div style={{ fontSize: '8pt', color: '#666' }}>Expired</div>
          </div>
          <div style={{ flex: 1, textAlign: 'center' }}>
            <div style={{ fontSize: '18pt', fontWeight: 'bold', color: '#6b7280' }}>{missing.length}</div>
            <div style={{ fontSize: '8pt', color: '#666' }}>Missing</div>
          </div>
        </div>

        {/* Table of children */}
        <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '10px', fontSize: '9pt' }}>
          <thead>
            <tr>
              <th style={{ background: '#e0e7ff', color: '#1e40af', padding: '6px 8px', textAlign: 'left', border: '1px solid #c7d2fe' }}>#</th>
              <th style={{ background: '#e0e7ff', color: '#1e40af', padding: '6px 8px', textAlign: 'left', border: '1px solid #c7d2fe' }}>Child Name</th>
              <th style={{ background: '#e0e7ff', color: '#1e40af', padding: '6px 8px', textAlign: 'center', border: '1px solid #c7d2fe' }}>Report Completed</th>
              <th style={{ background: '#e0e7ff', color: '#1e40af', padding: '6px 8px', textAlign: 'center', border: '1px solid #c7d2fe' }}>Expires</th>
              <th style={{ background: '#e0e7ff', color: '#1e40af', padding: '6px 8px', textAlign: 'center', border: '1px solid #c7d2fe' }}>Status</th>
            </tr>
          </thead>
          <tbody>
            {fullTimeChildren
              .sort((a, b) => {
                const order = { missing: 0, expired: 1, expiring: 2, compliant: 3 };
                return (order[a.status] ?? 4) - (order[b.status] ?? 4) || (a.childName || '').localeCompare(b.childName || '');
              })
              .map((child, i) => (
              <tr key={child.childId} style={{ background: i % 2 === 0 ? '#fff' : '#f9fafb' }}>
                <td style={{ padding: '5px 8px', border: '1px solid #e5e7eb', textAlign: 'center', color: '#999' }}>{i + 1}</td>
                <td style={{ padding: '5px 8px', border: '1px solid #e5e7eb', fontWeight: 500 }}>{child.childName}</td>
                <td style={{ padding: '5px 8px', border: '1px solid #e5e7eb', textAlign: 'center' }}>
                  {child.completedDate || '—'}
                </td>
                <td style={{ padding: '5px 8px', border: '1px solid #e5e7eb', textAlign: 'center' }}>
                  {child.expirationDate || '—'}
                </td>
                <td style={{ 
                  padding: '5px 8px', 
                  border: '1px solid #e5e7eb', 
                  textAlign: 'center', 
                  fontWeight: 'bold',
                  color: child.status === 'compliant' ? '#16a34a' : child.status === 'expiring' ? '#d97706' : '#dc2626'
                }}>
                  {child.status === 'compliant' ? 'CURRENT' : child.status === 'expiring' ? 'EXPIRING' : child.status === 'expired' ? 'EXPIRED' : 'MISSING'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ChildServiceReportSummaryTab;
